import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    untracked,
} from '@angular/core';
import type { ApiTypes } from '@hyundai/ng-common-lib';
import {
    AnchorDirective,
    AppFeature,
    DealerFeature,
    EventTrackingService,
    FootnotesPipe,
    ModelsFeature,
    RouterLinkDirective,
    TrackingDirective,
    TrackingFeature,
} from '@hyundai/ng-common-lib';
import {
    M030C100BigShortFactsComponent,
    M030C110ModelHeroComponent,
    M030C90GalleryComponent,
    M040C015WarrentyFooterLogosComponent,
    M040C020RteComponent,
    M040C040PromotionBannerComponent,
    M040C045OfferBannerComponent,
    M040C130ContactModelBannerComponent,
    M060C020VehicleLeasingExampleComponent,
} from '@hyundai/ui';
import { SimilarVehiclesComponent } from '@hyundai/vehicle-market-lib';
import { Store } from '@ngrx/store';
import { GlobalContactComponent } from '../../../components/global-contact/global-contact.component';

@Component({
    selector: 'app-model-detail-page',
    standalone: true,
    templateUrl: './model-detail-page.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        GlobalContactComponent,
        M030C100BigShortFactsComponent,
        M030C110ModelHeroComponent,
        M030C90GalleryComponent,
        M040C015WarrentyFooterLogosComponent,
        M040C020RteComponent,
        M040C045OfferBannerComponent,
        M040C130ContactModelBannerComponent,
        M060C020VehicleLeasingExampleComponent,
        NgClass,
        AnchorDirective,
        FootnotesPipe,
        NgTemplateOutlet,
        TrackingDirective,
        RouterLinkDirective,
        SimilarVehiclesComponent,
        M040C040PromotionBannerComponent,
        TrackingDirective,
    ],
})
export default class ModelDetailPageComponent {
    tracking = inject(EventTrackingService);
    store = inject(Store);

    model = this.store.selectSignal(ModelsFeature.state.selectLoadedModelDetail);
    dealer = this.store.selectSignal(DealerFeature.state.selectLoadedDealer);
    dealerContactMailLink = this.store.selectSignal(DealerFeature.state.selectDealerContactFormUrl);
    dealerLocationHeadline = this.store.selectSignal(DealerFeature.state.selectDealerLocationHeadline);
    showOnlyPrimaryButton = this.store.selectSignal(AppFeature.state.selectIsOpen);

    testDriveModelName = computed(() => {
        const model = this.model();
        if (!model.alternativeModelDetailName) return `Hyundai ${model.name}`;

        return model.alternativeModelDetailName.replace('Der neue', 'neuen');
    });

    firstCampaignWithMinimumShortTitle = computed<
        (ApiTypes.ModelCampaign & { content: { minimumShortTitle: string } }) | null
    >(() => {
        if (this.model() && this.model().campaigns && this.model().campaigns.length > 0) {
            const campaign = this.model().campaigns[0];
            if (this.hasMinimumShortTitle(campaign.content)) {
                return campaign as ApiTypes.ModelCampaign & { content: { minimumShortTitle: string } };
            }
        }
        return null;
    });

    hasMinimumShortTitle(
        content: ApiTypes.ModelCampaignContent,
    ): content is { __typename: 'AfterSale' | 'SalesCampaignContent'; minimumShortTitle: string } {
        return (
            content !== null &&
            content !== undefined &&
            (content.__typename === 'AfterSale' || content.__typename === 'SalesCampaignContent') &&
            'minimumShortTitle' in content
        );
    }

    constructor() {
        effect(() => {
            const model = this.model();
            if (model) {
                untracked(() => {
                    const models = [{ name: model.name }];
                    this.store.dispatch(TrackingFeature.actions.setModelsOnPageTracking({ models }));
                });
            }
        });
    }
}
